import { useMemo } from "react";

import { WidgetFrame } from "@/components/layout/WidgetFrame";
import { Chip } from "@/components/ui/Chip";
import { LinkIcon } from "@/components/ui/Icons";
import { Link } from "@/components/ui/Link";
import { Tooltip } from "@/components/ui/Tooltip/Tooltip";
import {
    Column,
    createChipsColumn,
    createDataColumn,
    createDataSortableColumn,
    useSort,
} from "@/components/ui/table-final-saviour/Table";
import { useSearch } from "@/components/ui/table-final-saviour/Table/hooks/useSearch";
import { EnrichedInsight } from "@/pages/DashboardPage/fms/widgets/ActionListTableWidget/action-list.types";
import {
    calculateInsightsWeight,
    getInsightSeverityColor,
    getUniqueInsightsWithMaxSeverity,
} from "@/pages/DashboardPage/fms/widgets/ActionListTableWidget/action-list.utils";
import { routes } from "@/routes";
import { generatePath } from "@/routes/routes.utils";
import { useGetCustomerInsightsQuery } from "@/store/services/insights.service";

export const ActionListTableWidget = () => {
    const dataSource = useGetCustomerInsightsQuery();

    // Extend data with number of insights per row & severity summary
    const enrichedData = useMemo(() => {
        if (!dataSource.data) return [];

        // Initial sort by customer name
        const sortedData = dataSource.data.toSorted((a, b) => a.customerName.localeCompare(b.customerName));

        return sortedData.map((item) => {
            return {
                ...item,
                insightsWeight: calculateInsightsWeight(item.insights),
                insightTypes: item.insights.map((insight) => insight.type).join(", "),
            };
        });
    }, [dataSource.data]);

    const { sortControls, applySort } = useSort<EnrichedInsight>({
        initialSortConfig: { key: "insightsWeight", dataType: "number", order: "desc" },
    });

    const columns = useMemo(
        () => [
            createDataSortableColumn({
                dataKey: "customerName",
                dataType: "string",
                title: "Customer Name",
                width: 200,
                ...sortControls,
                getCellValue: ({ row }) => {
                    const label = row.customerName;

                    const link = generatePath(routes.customerDetail, {
                        subRoute: routes.customerDetail.subRoutes.milking,
                        params: {
                            customerNumber: row.customerNumber,
                        },
                    });

                    if (link) {
                        return <Link to={link}>{label}</Link>;
                    }

                    return label;
                },
            }),
            createChipsColumn({
                title: "Connection Performance Device Index Insights",
                getChips: ({ row: { insights, customerNumber } }) => {
                    const linkTo = generatePath(routes.customerDetail, {
                        subRoute: routes.customerDetail.subRoutes.milking,
                        subSubRoute: routes.customerDetail.subRoutes.milking.subRoutes.connectionPerformance,
                        params: {
                            customerNumber: customerNumber,
                        },
                    });

                    // Sort unique insights alphabetically by type
                    const sortedUniqueInsights = getUniqueInsightsWithMaxSeverity(insights)
                        .slice()
                        .sort((a, b) => a.type.localeCompare(b.type));

                    return sortedUniqueInsights.map((insight, index) => {
                        const insightDisplayName = insight.data.displayName ?? insight.type;
                        const insightCount = insights.filter((i) => i.type === insight.type).length;

                        const content = (
                            <Tooltip
                                content={`${insightDisplayName} in ${insightCount} device${insightCount !== 1 ? "s" : ""}`}
                            >
                                <Chip
                                    key={index}
                                    variant={"outlined"}
                                    color={getInsightSeverityColor(insight.severity)}
                                    endIcon={<LinkIcon />}
                                >
                                    <>{insightDisplayName}</>
                                </Chip>
                            </Tooltip>
                        );
                        return linkTo ? (
                            <Link key={index} to={linkTo}>
                                {content}
                            </Link>
                        ) : (
                            content
                        );
                    });
                },
                width: 400,
            }),
        ],
        [sortControls],
    ) as Column<EnrichedInsight>[];

    const searchColumns: Column<EnrichedInsight>[] = useMemo(
        () => [
            ...columns,
            createDataColumn({
                dataKey: "insightTypes",
                title: "Insight Types",
                dataType: "string",
            }),
        ],
        [columns],
    );

    const { searchValue, setSearchValue, applySearch } = useSearch({
        columns: searchColumns,
    });

    const searchedData = useMemo(() => {
        return applySort(applySearch(enrichedData));
    }, [applySort, applySearch, enrichedData]);

    return (
        <>
            <WidgetFrame.Title title={"Action List"} icon={"lely-icon-show"}>
                <WidgetFrame.Title.Search value={searchValue} setValue={setSearchValue} />
                <WidgetFrame.Title.Information pageId={2145779720} />
            </WidgetFrame.Title>
            <WidgetFrame.Table dataSource={{ ...dataSource, data: searchedData }} columns={columns} maxHeight={800} />
        </>
    );
};
